import Quill from 'quill';
import type { Range } from 'quill';
import Embed from 'quill/blots/embed';
import QuillTableBetter from 'quill-table-better';
import { defaultModules } from 'ngx-quill';
import { rootInjector } from './injector-holder';
import { QuillImageUploadService } from '../services/quill-image-upload.service';

// Register quill-table-better before any Quill editor is created
Quill.register(
    {
        'modules/table-better': QuillTableBetter,
    },
    true
);

/** Custom Quill image handler: uploads file to backend via FormData POST, then inserts img tag with URL = baseUrl + '/' + fileName. */
function createImageHandler(): () => void {
    return function (this: { quill: any; container: HTMLElement }) {
        const quill = this.quill;

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept =
            'image/png,image/gif,image/jpeg,image/jpg,image/bmp,image/x-icon,image/webp';
        fileInput.style.display = 'none';

        fileInput.addEventListener('change', async () => {
            const file = fileInput.files?.[0];
            if (!file) {
                fileInput.remove();
                return;
            }

            const uploadService = rootInjector?.get(QuillImageUploadService);
            if (!uploadService) {
                console.error('QuillImageUploadService not available');
                fileInput.remove();
                return;
            }

            try {
                const imageUrl = await uploadService.uploadImage(file);
                const range = quill.getSelection(true);

                quill.insertEmbed(range.index, 'image', imageUrl, 'user');
                quill.setSelection(range.index + 1);
            } catch {
                // handled elsewhere
            } finally {
                fileInput.remove();
            }
        });

        document.body.appendChild(fileInput);
        fileInput.click();
    };
}

/**
 * Re-applies HTML content so quill-table-better tables render correctly; clears first to avoid
 * duplicating ngx-quill's writeValue content. See quill-table-better issue #12.
 *
 * The reapply must stay USER-sourced (not SILENT) — table-better's cell-selection overlay only
 * attaches on a non-silent change, and SILENT here left a stray selected-cell outline visible in
 * read-only previews. But Quill's `modify()` wrapper (core/quill.js) no-ops any USER-sourced change
 * while the editor is disabled, which on a disabled/read-only instance (e.g. hideActions views)
 * dropped the reapply entirely after the SILENT clear, leaving the editor blank. So briefly re-enable
 * around just the updateContents call, then restore — synchronous, so there's no visible flash.
 */
export function applyQuillTableBetterContent(quill: {
    clipboard: { convert: (arg: { html: string }) => { length: number } };
    selection: { getRange: () => [unknown, number?] };
    setContents: (delta: unknown, source?: string) => unknown;
    updateContents: (delta: unknown, source: string) => unknown;
    setSelection: (index: number, source: string) => void;
    scrollIntoView?: () => void;
    isEnabled?: () => boolean;
    enable?: (enabled?: boolean) => void;
}, html: string): void {
    if (!html || typeof html !== 'string' || !html.includes('<table')) return;
    const delta = quill.clipboard.convert({ html }) as unknown;
    const [range] = quill.selection.getRange();
    const deltaLen = typeof (delta as { length?: number | (() => number) }).length === 'function'
        ? ((delta as { length: () => number }).length)()
        : ((delta as { length?: number }).length ?? 0);
    const rangeLen = range != null && typeof range === 'object' && 'length' in range ? (range as { length: number }).length : 0;
    // Clear existing content first so we replace instead of merge (avoids duplicating paragraphs/tables)
    const emptyDelta = { ops: [] };
    quill.setContents(emptyDelta as Parameters<typeof quill.setContents>[0], Quill.sources.SILENT);
    const wasDisabled = typeof quill.isEnabled === 'function' && !quill.isEnabled();
    if (wasDisabled) quill.enable?.(true);
    quill.updateContents(delta, Quill.sources.USER);
    if (wasDisabled) quill.enable?.(false);
    quill.setSelection(Math.max(0, deltaLen - rangeLen), Quill.sources.SILENT);
    if (typeof quill.scrollIntoView === 'function') quill.scrollIntoView();
}

export const quillModules = {
    ...defaultModules,
    table: false, // disable default table module; we use table-better only
    'table-better': {
        language: 'en_US',
        menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
        toolbarTable: true,
    },
    keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
    },
    toolbar: {
        container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            // [{ font: [] }],
            [{ size: ['small', false, 'large'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ align: [] }, { color: [] }, { background: [] }],
            ['table-better', { script: 'sub' }, { script: 'super' }, { list: 'bullet' }, { list: 'ordered' }, { indent: '-1' }, { indent: '+1' }],
            ['blockquote', 'code-block'],
            ['link', 'image', 'video'],
            ['clean'],
        ],
        handlers: {
            image: createImageHandler(),
        },
    },
};

const SOFT_BREAK_CLASS = 'ql-softbreak';

/** `<br class="ql-softbreak">` embed: stays inside its block instead of splitting it, unlike Quill's
 * built-in placeholder Break (bare `<br>`, kept separate via className). Extends Quill's own `Embed`
 * (quill/blots/embed) rather than plain Parchment EmbedBlot — Embed adds left/right guard text nodes
 * around the embed that stabilize cursor placement; several Quill v2 reports of a plain EmbedBlot
 * failing to register or misbehaving at the cursor trace back to using the wrong base class. */
class SoftBreak extends Embed {}
SoftBreak.blotName = 'softbreak';
SoftBreak.tagName = 'BR';
SoftBreak.className = SOFT_BREAK_CLASS;
Quill.register({ 'formats/softbreak': SoftBreak }, true);

/** Clipboard matcher: round-trips our own `<br class="ql-softbreak">` back to the embed; any other `<br>` keeps Quill's normal hard-newline paste behavior. */
function matchSoftBreak(node: Node, delta: { ops: { insert?: unknown }[] }): typeof delta {
    if (!(node instanceof HTMLElement) || !node.classList.contains(SOFT_BREAK_CLASS)) {
        return delta;
    }
    const last = delta.ops[delta.ops.length - 1];
    if (last?.insert === '\n') delta.ops.pop();
    return delta;
}

/** Clipboard matcher: drops pasted images/videos entirely — the policy editor doesn't allow embedding media. */
function stripMediaNode() {
    return new Delta();
}

/** Converts `\n` to soft breaks on plain-text-only paste (matchers never run there); replaces the clipboard module globally, but only acts when `policySoftBreak` is set — every other editor gets stock behavior. */
const Clipboard = Quill.import('modules/clipboard') as any;
const Delta = Quill.import('delta');
class PolicyClipboard extends Clipboard {
    convert(
        input: { html?: string; text?: string },
        formats: Record<string, unknown> = {}
    ) {
        if (
            !input.html &&
            input.text &&
            this['options'].policySoftBreak &&
            !formats['code-block']
        ) {
            const delta = new Delta();
            input.text.split('\n').forEach((line: string, i: number) => {
                if (i > 0) delta.insert({ softbreak: true });
                if (line) delta.insert(line, formats);
            });
            return delta;
        }
        return super.convert(input, formats);
    }
}
Quill.register('modules/clipboard', PolicyClipboard, true);

/** Shift+Enter inserts a soft break instead of splitting the current block. A trailing zero-width
 * space anchors the cursor right after the <br> embed — without it, a contenteditable can leave the
 * caret ambiguously positioned before/after the break. */
function createSoftBreakBinding() {
    return {
        key: 'Enter',
        shiftKey: true,
        handler(this: { quill: Quill }, range: Range) {
            const delta = new Delta()
                .retain(range.index)
                .delete(range.length)
                .insert({ softbreak: true });
            this.quill.updateContents(delta, Quill.sources.USER);
            this.quill.insertText(range.index + 1, '\u200B', Quill.sources.USER);
            this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
            return false;
        },
    };
}

export const policyQuillModules = {
    ...defaultModules,
    table: false, // disable default table module; we use table-better only
    'table-better': {
        language: 'en_US',
        menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
        toolbarTable: true,
    },
    keyboard: {
        bindings: {
            ...QuillTableBetter.keyboardBindings,
            softBreak: createSoftBreakBinding(),
        },
    },
    toolbar: {
        container: [
            [{ header: [2, 3, 4, 5, 6, false] }],
            // [{ font: [] }],
            [{ size: ['small', false, 'large'] }],
            ['bold', 'italic', 'strike'],
            [{ align: [] }], // color/background excluded: getSemanticHTML() (ngx-quill's HTML export) drops inline color/background styles, so they never survive into the saved value
            ['table-better', { script: 'sub' }, { script: 'super' }, { list: 'bullet' }, { list: 'ordered' }, { indent: '-1' }, { indent: '+1' }],
            ['blockquote', 'code-block'],
            ['link'],
            ['clean'],
        ],
    },
    // No image/video upload path: toolbar button removed above, drag-drop and
    // clipboard image blobs disabled via mimetypes, and pasted <img>/<iframe>/
    // <video> markup stripped via the clipboard matchers below.
    uploader: {
        mimetypes: [],
    },
    clipboard: {
        matchers: [
            ['br', matchSoftBreak],
            ['img', stripMediaNode],
            ['iframe', stripMediaNode],
            ['video', stripMediaNode],
        ],
        policySoftBreak: true,
    },
};
